import { mapSearchStateToAiSearchContext } from '@pkgs/components/utils/tracking';
import { type PageType, type TemplateType } from '@pkgs/components/types/tracking';
import { SearchMode } from '@pkgs/components/hooks/stores/search/slices/searchModeSlice';
import {
  type Language,
  type MarketCountryCode,
  type MeasurementSystem,
  type SearchListingsFilters,
  type GenericExposeListing,
  type MasterDataShopResponse,
} from '@pkgs/api';
import {
  getABTestContext,
  getAiSearchContext,
  getNavigationContext,
  getPageContext,
  getPropertyContext,
  getShopContext,
  trackNavigationEvent,
  trackUserInteractionEvent,
  type UserActionType,
} from '@ev-tech/snowplow-library';

import { getCookiesConsentContext, getShopContextCommonValues, mapListingToPropertyContext } from '@/utils/snowplowContext';
import { type NavigationTrackingContext } from '@/types/tracking';

type UserInteractionData = {
  elementId: string;
  action: UserActionType;
};

export const getMapABTestContext = (isMapToggledOn: boolean) => {
  return getABTestContext({
    id: 'isMapToggledOn',
    name: 'isMapToggledOn',
    variant: isMapToggledOn ? 'true' : 'false',
    variant_description: 'map initially toggled on or off',
  });
};

export const trackNavigationCustomEvent = ({
  navigationData,
  language,
  countryCode,
  listing,
  shop,
  pageType,
  templateType,
  filters,
  isMapToggledOn,
  measurementSystem,
  searchMode,
}: {
  navigationData: NavigationTrackingContext;
  language: Language;
  countryCode: MarketCountryCode;
  listing?: GenericExposeListing;
  shop?: MasterDataShopResponse;
  pageType?: PageType;
  templateType?: TemplateType;
  filters?: SearchListingsFilters;
  isMapToggledOn?: boolean;
  measurementSystem: MeasurementSystem;
  searchMode: SearchMode;
}) => {
  const { href, elementId, targetScreen, sourceScreen, selectedOption } = navigationData;

  const navigationContext = getNavigationContext({
    target_url: href,
    element_id: elementId,
    source_screen: sourceScreen,
    target_screen: targetScreen,
    selected_option: selectedOption?.toLowerCase(),
  });

  const pageContext = getPageContext({
    isHQPage: true,
    type: pageType,
    template_type: templateType,
    page_language: language,
    country_code: countryCode,
  });

  const shopContext =
    listing && 'trackingData' in listing
      ? getShopContext(getShopContextCommonValues({ listing, shop }))
      : filters
        ? getShopContext({
            shop_id: shop?.id ?? filters.masterDataShopIds?.[0],
            license_id: shop?.lipaId ?? filters.masterDataLiPaIds?.[0],
            business_division_name: filters.businessArea[0],
          })
        : undefined;

  trackNavigationEvent({
    screenName: '',
    contexts: [
      navigationContext,
      pageContext,
      ...(shopContext ? [shopContext] : []),
      ...(listing ? [getPropertyContext(mapListingToPropertyContext(listing, measurementSystem))] : []),
      getCookiesConsentContext(),
      ...(isMapToggledOn === undefined ? [] : [getMapABTestContext(isMapToggledOn)]),
      getAiSearchContext(
        mapSearchStateToAiSearchContext({
          preferences: filters?.preferences,
          isAiSearchEnabled: searchMode === SearchMode.AI,
        }),
      ),
    ],
  });
};

export const trackUserInteraction = ({
  userInteractionData,
  listing,
  shop,
  pageType,
  templateType,
  language,
  countryCode,
  filters,
  isMapToggledOn,
  searchMode,
}: {
  userInteractionData: UserInteractionData;
  listing?: GenericExposeListing;
  shop?: MasterDataShopResponse;
  pageType?: PageType;
  templateType?: TemplateType;
  language: Language;
  countryCode: MarketCountryCode;
  filters?: SearchListingsFilters;
  isMapToggledOn?: boolean;
  searchMode?: SearchMode;
}) => {
  const { action, elementId } = userInteractionData;

  const pageContext = getPageContext({
    isHQPage: true,
    type: pageType,
    template_type: templateType,
    page_language: language,
    country_code: countryCode,
  });

  const shopContext =
    listing && 'trackingData' in listing
      ? getShopContext(getShopContextCommonValues({ listing, shop }))
      : filters
        ? getShopContext({
            id: filters.shopIds?.[0],
            business_division_name: filters.businessArea[0],
            license_id: shop?.lipaId,
            shop_id: shop?.id,
          })
        : undefined;

  trackUserInteractionEvent({
    action,
    elementId,
    contexts: [
      pageContext,
      ...(shopContext ? [shopContext] : []),
      getCookiesConsentContext(),
      ...(isMapToggledOn === undefined ? [] : [getMapABTestContext(isMapToggledOn)]),
      getAiSearchContext(
        mapSearchStateToAiSearchContext({
          preferences: filters?.preferences,
          isAiSearchEnabled: searchMode ? searchMode === SearchMode.AI : undefined,
        }),
      ),
    ],
  });
};
